import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import TeamColourDot from 'components/admin/TeamColourDot';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import TableActionButtons from 'components/NewDesign/TableActionButtons';
import { formatPoints } from 'utils/points';
import { formatUpdatedAt } from 'utils/meetPoints';
import { bonusPointsLineText, bonusPointsProblem, housesInOwnOrder, parseBonusPoints } from 'utils/bonusPoints';
import styles from './BonusPoints.module.css';

// The Bonus Points card on the Points page, under House Totals (points slice 4c,
// docs/school-carnivals-points-slice-4c.md in the portal repo): the lines an Admin adds to a house for what
// the timing cannot score, negative for a deduction, oldest first. Each counts in its house's Bonus and Total
// on the House Totals card. An Admin adds, changes and deletes lines and sees who added each; a school's
// members, once the points are released, see the lines read only, and no card when there are none (the page
// decides). Every word agreed by Doug, 2026-10-26.
//
// The card opens collapsed, its header row only (Doug, 2026-10-26, C2: "large and empty and take up
// important space ... They see the bonus points in the house table anyway. So expand when they want to see
// the details only"), as Edit Meet's volunteer requirement sections do: a chevron, the title and the number
// of lines; a click opens it. Inside, + Add Bonus Points is the quiet dashed row of their "Add role", below
// the table ("The big loud blue + Add bonus points button is dominating the page").

const emptyForm = { teamId: '', points: '', reason: '' };

const BonusPoints = ({ lines, houses, teamWords, isAdmin, onSave, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(null); // null closed; { lineId } open, lineId null when adding
  const [form, setForm] = useState(emptyForm);
  const [problem, setProblem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null); // the line asked about
  const [deleteBusy, setDeleteBusy] = useState(false);

  const houseById = Object.fromEntries((houses || []).map((house) => [house.teamId, house]));
  const ownOrder = housesInOwnOrder(houses);

  const openAdd = () => {
    setForm(emptyForm);
    setProblem(null);
    setEditing({ lineId: null });
  };

  const openChange = (line) => {
    setForm({ teamId: line.teamId, points: String(line.points), reason: line.reason });
    setProblem(null);
    setEditing({ lineId: line.lineId });
  };

  const closePopup = () => {
    if (!saving) setEditing(null);
  };

  const handleSave = async () => {
    const found = bonusPointsProblem(form, teamWords);
    if (found) {
      setProblem(found);
      return;
    }
    setSaving(true);
    try {
      await onSave(editing.lineId, {
        teamId: form.teamId,
        points: parseBonusPoints(form.points),
        reason: form.reason.trim(),
      });
      setEditing(null);
    } catch (err) {
      console.error('[BonusPoints] Save error: ', err);
      // The server's own check, when it finds one; otherwise a general message.
      const message = err.response?.data?.message;
      if (message) setProblem(message);
      else toast.error('Could not save the bonus points.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteBusy(true);
    try {
      await onDelete(deleting);
      setDeleting(null);
    } catch (err) {
      console.error('[BonusPoints] Delete error: ', err);
      toast.error('Could not delete the bonus points.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const columns = isAdmin ? 5 : 3;

  return (
    <div className={styles.card}>
      <button
        type="button"
        className={`${styles.header} ${expanded ? styles.headerOpen : ''}`}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown size={20} className={styles.chevron} />
        ) : (
          <ChevronRight size={20} className={styles.chevron} />
        )}
        <h3 className={styles.title}>Bonus Points</h3>
        {lines.length > 0 && <span className={styles.count}>{lines.length}</span>}
      </button>
      {expanded && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{teamWords.team}</th>
                <th className={styles.number}>Points</th>
                <th>Reason</th>
                {isAdmin && <th>Added by</th>}
                {isAdmin && <th className={styles.actions} aria-label="Change or delete" />}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={columns} className={styles.empty}>
                    No bonus points yet.
                  </td>
                </tr>
              )}
              {lines.map((line) => {
                const house = houseById[line.teamId];
                return (
                  <tr key={line.lineId}>
                    <td className={styles.name}>
                      <span className={styles.nameInner}>
                        <TeamColourDot colour={house?.colour} />
                        {house?.name}
                      </span>
                    </td>
                    <td className={`${styles.number} ${styles.points}`}>{formatPoints(line.points)}</td>
                    <td className={styles.reason}>{line.reason}</td>
                    {isAdmin && (
                      <td className={styles.addedBy}>
                        {line.addedBy}
                        <span className={styles.addedAt}>{formatUpdatedAt(line.addedAt)}</span>
                      </td>
                    )}
                    {isAdmin && (
                      <td className={styles.actions}>
                        {TableActionButtons(line, openChange, setDeleting, {
                          editTitle: 'Change these bonus points',
                          deleteTitle: 'Delete these bonus points',
                        })}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isAdmin && (
            <button type="button" className={styles.addRow} onClick={openAdd}>
              <Plus size={16} />
              <span>Add Bonus Points</span>
            </button>
          )}
        </div>
      )}

      <CustomNewConfirm
        isOpen={editing !== null}
        onClose={closePopup}
        title={editing?.lineId ? 'Change Bonus Points' : 'Add Bonus Points'}
        confirmButtonText="Save"
        onConfirm={handleSave}
        isLoading={saving}
      >
        <div className={styles.form}>
          <label className={styles.field}>
            <span className={styles.label}>{teamWords.team}</span>
            <select
              className={styles.input}
              value={form.teamId}
              onChange={(e) => setForm({ ...form, teamId: e.target.value })}
            >
              <option value="" />
              {ownOrder.map((house) => (
                <option key={house.teamId} value={house.teamId}>
                  {house.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Points</span>
            <input
              type="number"
              step="0.5"
              className={styles.input}
              value={form.points}
              onChange={(e) => setForm({ ...form, points: e.target.value })}
            />
            {/* Doug, 2026-10-26 (C3): say how to deduct points; the words are my recommendation, at his request,
                with his -5.5 so the example shows half points too. */}
            <span className={styles.hint}>Enter a negative number to deduct points, for example -5.5.</span>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Reason</span>
            <input
              type="text"
              maxLength={200}
              className={styles.input}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>
          {problem && <p className={styles.problem}>{problem}</p>}
        </div>
      </CustomNewConfirm>

      <CustomNewConfirm
        isOpen={deleting !== null}
        onClose={() => !deleteBusy && setDeleting(null)}
        title="Delete these bonus points?"
        description={
          deleting
            ? bonusPointsLineText(houseById[deleting.teamId]?.name || '', deleting.points, deleting.reason)
            : ''
        }
        confirmButtonText="Delete"
        confirmButtonVariant="danger"
        onConfirm={handleDelete}
        isLoading={deleteBusy}
      />
    </div>
  );
};

BonusPoints.propTypes = {
  // MeetPointsDashboardDto.bonusPoints: each line's id, house, points and reason; for an Admin, who added it.
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      lineId: PropTypes.string,
      teamId: PropTypes.string,
      points: PropTypes.number,
      reason: PropTypes.string,
      addedBy: PropTypes.string,
      addedAt: PropTypes.string,
    })
  ).isRequired,
  // MeetPointsDashboardDto.houses: each house's name, colour and place in the school's own order.
  houses: PropTypes.arrayOf(
    PropTypes.shape({ teamId: PropTypes.string, name: PropTypes.string, colour: PropTypes.string, order: PropTypes.number })
  ),
  // The school's words for a house (utils/teamLabel.js).
  teamWords: PropTypes.shape({
    team: PropTypes.string,
    lower: PropTypes.shape({ team: PropTypes.string }),
  }).isRequired,
  isAdmin: PropTypes.bool,
  // Saves a line: (lineId, { teamId, points, reason }), lineId null for a new one. Rejects on failure.
  onSave: PropTypes.func.isRequired,
  // Deletes a line. Rejects on failure.
  onDelete: PropTypes.func.isRequired,
};

export default BonusPoints;
